import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useSWRConfig } from 'swr';
import { chatRoomKeys } from './useChatRooms';

/**
 * Returns a function that simultaneously:
 *   1. Invalidates every React Query chat-room list (covers ChatRoomsTrigger,
 *      InboxSidePanel, ChatRoomList, ChatRoom row badges, "Inbox (N)" header).
 *   2. Revalidates SWR chat list + room keys (`admin/account-managers/chat-rooms`
 *      and `chatRoom/<id>`), covering inbox list and channel-scoped room views.
 *
 * Call this after any action that changes read-state or message state.
 */
export function useInvalidateChatRooms() {
  const queryClient = useQueryClient();
  const { mutate: globalSwrMutate } = useSWRConfig();

  return useCallback(
    (chatRoomId?: string) => {
      queryClient.invalidateQueries({
        queryKey: [chatRoomKeys.FETCH_CHAT_ROOMS],
      });

      globalSwrMutate(
        (key: unknown) =>
          typeof key === 'string' &&
          key.startsWith('admin/account-managers/chat-rooms')
      );

      if (chatRoomId) {
        const prefix = `chatRoom/${chatRoomId}`;
        // Call without a data argument: SWR revalidates matching keys in the
        // background while keeping stale data visible until the response arrives.
        // Passing `undefined` as data would clear the cache and cause a flash.
        globalSwrMutate(
          (key: unknown) => typeof key === 'string' && key.startsWith(prefix)
        );
      }
    },
    [queryClient, globalSwrMutate]
  );
}
